import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { probeEnedis } from "@/api/sites";
import { SITES_LIST_QUERY_KEY } from "@/hooks/useSites";
import type { Site } from "@/types";

/**
 * Réconcilie le cache react-query avec un `Site` réévalué (S14-7, A2/A8).
 *
 * La réponse 200 du probe (`POST …/enedis/probe`) **remplace** l'entrée de
 * cache — **PAS de re-fetch de la liste** (§ 4.bis, § 9.bis) :
 * - dans `['sites', 'list']`, on substitue l'item de même `id` ;
 * - on pose aussi le détail `['sites', id]` au cas où il serait monté ailleurs.
 *
 * Exportée pour réutilisation (warm-up, lazy) — toutes les écritures de cache
 * issues d'un probe passent par ce point unique (réconciliation owner-scopée,
 * leçon mémoire `cache_reconciliation_identity_mutations`).
 */
function replaceSiteInCache(
  queryClient: QueryClient,
  site: Site,
): void {
  queryClient.setQueryData<Site[]>(SITES_LIST_QUERY_KEY, (prev) =>
    prev ? prev.map((s) => (s.id === site.id ? site : s)) : prev,
  );
  queryClient.setQueryData<Site>(["sites", site.id], site);
}

/**
 * Mutation `POST /api/v1/sites/{id}/enedis/probe` (S14-7, déclencheur de probe
 * S14-4). Corps vide ; la réponse 200 est le `SiteRead` réévalué.
 *
 * Au succès, **remplace** l'entrée de cache (cf. `replaceSiteInCache`) :
 * - **succès** (200, `prm_validated: true`) → statut « validé » figé session ;
 * - **échec consentement/PRM** (200, `prm_validated: false`) → statut effacé ;
 * - **indéterminé** (200, site inchangé) → statut tel quel (jamais de faux
 *   « non validé »).
 *
 * Les erreurs HTTP (400 `ENEDIS_PRM_NOT_SET`, 403 `ENEDIS_REQUIRES_ACCOUNT`,
 * 404 `SITE_NOT_FOUND`) sont **propagées sans toucher au cache** ; le call-site
 * (warm-up / lazy) les avale silencieusement (le probe est non bloquant).
 */
export function useEnedisProbe(): UseMutationResult<Site, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation<Site, unknown, string>({
    mutationFn: (siteId) => probeEnedis(siteId),
    onSuccess: (site) => {
      replaceSiteInCache(queryClient, site);
    },
  });
}
